import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AiFillHome } from 'react-icons/ai';
import { FiLock, FiShield, FiDatabase, FiCheckCircle, FiUser, FiUsers, FiBriefcase, FiMessageCircle, FiVideo, FiActivity, FiInstagram } from 'react-icons/fi';
import {
  WelcomeContainer,
  Header,
  HeaderContainer,
  Logo,
  LogoSJ,
  LogoMate,
  NavLinks,
  NavLink,
  NavHomeIcon,
  NavButtons,
  SignInButton,
  PrimaryButton,
  SecondaryButton,
  DashboardButton,
  DashboardBadge,
  Section,
  SectionTitle,
  SectionSubtitle,
  HeroSection,
  HeroContent,
  HeroTitle,
  HeroSubtitle,
  HeroButtons,
  HeroGraphContainer,
  HeroImageWrapper,
  HeroImage,
  TaglineSection,
  TaglineTitle,
  TaglineSubline,
  ThreeColumnGrid,
  FeatureCard,
  FeatureCardTitle,
  FeatureCardIcon,
  FeatureCardList,
  FeatureCardItem,
  FeatureCardButtonWrapper,
  FeaturesGrid,
  FeatureSection,
  FeatureContentWrapper,
  FeatureTitle,
  FeatureDescription,
  FeatureImage,
  StepsContainer,
  StepCard,
  StepNumber,
  StepTitle,
  StepDescription,
  PricingGrid,
  PricingCard,
  PricingBadge,
  PricingTitle,
  PricingPrice,
  PricingPeriod,
  PricingFeatures,
  PricingFeature,
  PricingDiscountContainer,
  PricingOriginalPrice,
  PricingDiscountPrice,
  PricingDiscountBadgeTop,
  FAQContainer,
  FAQItem,
  FAQQuestion,
  FAQAnswer,
  TestimonialsGrid,
  TestimonialCard,
  TestimonialText,
  TestimonialAuthor,
  TestimonialRole,
  StatsContainer,
  StatCard,
  StatNumber,
  StatLabel,
  Footer,
  FooterContent,
  FooterSection,
  FooterLogo,
  FooterLogoSJ,
  FooterLogoMate,
  FooterTitle,
  FooterLink,
  FooterBottom,
  FooterLine,
  FooterText,
  FooterSocialLinks,
  FooterSocialLink,
  CTASection,
  CTATitle,
  CTASubtitle,
  CTAButton,
  CTANote,
  FreePlanSection,
  FreePlanContent,
  FreePlanButtonWrapper,
  FreePlanButton,
  FreePlanTitle,
  FreePlanText,
  ProblemSection,
  SolutionSection,
  ContactFormContainer,
  ContactForm,
  ContactFormGroup,
  ContactLabel,
  ContactInput,
  ContactTextarea,
  ContactSubmitButton,
  ContactSuccessMessage,
  ContactErrorMessage,
  ScrollAnimateStyles,
  SectionDivider,
  DividerLine,
  DividerAccent,
  DividerSquare,
  DividerHorizontalLine,
  SecurityGrid,
  SecurityItem,
  SecurityIcon,
  SecurityTitle,
  SecurityDescription,
} from './styles';

// Type assertion helper for icons to work with React 19
const Icon = ({ component: Component, ...props }: { component: any; [key: string]: any }) => {
  return <Component {...props} />;
};

const WelcomeScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Refs for scroll animations
  const heroRef = useRef<HTMLDivElement>(null);
  const problemRef = useRef<HTMLDivElement>(null);
  const solutionRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const stepsRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const faqRef = useRef<HTMLDivElement>(null);
  const freePlanRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const comingFeaturesRef = useRef<HTMLDivElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);
  const securityRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Intersection Observer hook for scroll animations
  useEffect(() => {
    let observer: IntersectionObserver | null = null;
    
    // Function to check if element is in viewport (more lenient check)
    const isInViewport = (element: Element): boolean => {
      const rect = element.getBoundingClientRect();
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;
      const windowWidth = window.innerWidth || document.documentElement.clientWidth;
      
      // Element is in viewport if any part of it is visible
      return (
        rect.top < windowHeight &&
        rect.bottom > 0 &&
        rect.left < windowWidth &&
        rect.right > 0
      );
    };

    // Initialize observer immediately
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    // Use requestAnimationFrame to ensure DOM is ready, then initialize
    requestAnimationFrame(() => {
      // Observe all elements with scroll-animate class
      const animatedElements = document.querySelectorAll('.scroll-animate');
      animatedElements.forEach((el) => {
        observer?.observe(el);
        // If element is already in viewport, animate immediately
        if (isInViewport(el)) {
          // Use setTimeout 0 to ensure CSS classes are applied first
          setTimeout(() => {
            el.classList.add('animate-in');
          }, 0);
        }
      });

      // Also observe section refs
      const refs = [
        heroRef, problemRef, solutionRef, featuresRef, 
        stepsRef, faqRef, freePlanRef, ctaRef, 
        comingFeaturesRef, contactRef
      ];

      refs.forEach((ref) => {
        if (ref.current) {
          observer?.observe(ref.current);
          if (isInViewport(ref.current)) {
            setTimeout(() => {
              ref.current!.classList.add('animate-in');
            }, 0);
          }
        }
      });
    });

    return () => {
      if (observer) {
        const animatedElements = document.querySelectorAll('.scroll-animate');
        animatedElements.forEach((el) => {
          observer?.unobserve(el);
        });
        const refs = [
          heroRef, problemRef, solutionRef, featuresRef, 
          stepsRef, faqRef, freePlanRef, ctaRef, 
          comingFeaturesRef, contactRef, securityRef
        ];
        refs.forEach((ref) => {
          if (ref.current) {
            observer?.unobserve(ref.current);
          }
        });
      }
    };
  }, []);

  const handleSignIn = () => {
    navigate('/signin');
  };

  const handleSignUp = () => {
    navigate('/signup');
  };

  const handleDashboard = () => {
    if (user?.role === 'coach') {
      navigate('/coach/home');
    } else if (user?.role === 'athlete') {
      navigate('/jumper/home');
    }
  };

  const handleLogoClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Scroll functions for navigation
  const scrollToHome = () => {
    navigate('/');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, location.pathname !== '/' ? 100 : 0);
  };

  const scrollToFeatures = () => {
    if (location.pathname !== '/') {
      navigate('/#features');
      setTimeout(() => {
        featuresRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } else {
      featuresRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToFAQ = () => {
    if (location.pathname !== '/') {
      navigate('/#faq');
      setTimeout(() => {
        faqRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } else {
      faqRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToContact = () => {
    if (location.pathname !== '/') {
      navigate('/#contact');
      setTimeout(() => {
        contactRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } else {
      contactRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };


  // Handle hash navigation when arriving from another page
  useEffect(() => {
    if (location.hash) {
      const hash = location.hash.substring(1); // Remove the #
      setTimeout(() => {
        switch (hash) {
          case 'features':
            featuresRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            break;
          case 'faq':
            faqRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            break;
          case 'contact':
            contactRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            break;
          default:
            break;
        }
      }, 300); // Give time for page to render
    }
  }, [location.hash]);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setContactForm(prev => ({ ...prev, [name]: value }));
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactForm),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setContactForm({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Hero graph data kept for potential future use (weight/distance/form tabs + Recharts)
  // const weightData = [ ... ];
  // const distanceData = [ ... ];
  // const formData = [ ... ];
  // const athleteColors = ['#0066cc', '#ff9600', '#00cc66', '#cc0066'];

  const faqs = [
    {
      question: 'Is sjMate really free to start?',
      answer: 'Yes! The free plan includes basic data entry and viewing. Upgrade anytime to unlock advanced analytics, training planner, equipment, competition features and AI Chat.'
    },
    {
      question: 'Can I cancel my subscription anytime?',
      answer: 'Absolutely. No long-term contracts. Cancel anytime from your account settings.'
    },
    {
      question: 'How do I import my training data?',
      answer: 'On Training Sessions page, click on START NEW TRAINING SESSION button and simply import your session data. First you import your Location, Type of training, Date, Time and Weather. After that you start importing each jump, where you can enter values for starting gate, distance, wind, inrun speed, equipment and Rating of a jump.'
    },
    {
      question: 'How do coaches and athletes connect their accounts?',
      answer: (
        <>
          Connecting accounts is easy! The process depends on who creates their account first. For detailed step-by-step instructions,{' '}
          <a 
            href="/how-to-start#connecting-accounts" 
            onClick={(e) => {
              e.preventDefault();
              navigate('/how-to-start#connecting-accounts');
              // Scroll to section after navigation
              setTimeout(() => {
                const element = document.getElementById('connecting-accounts');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 100);
            }}
            style={{ 
              color: 'var(--accent-orange)', 
              textDecoration: 'underline',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            visit our guide
          </a>
          .
        </>
      )
    },
    {
      question: 'Is my data secure?',
      answer: 'Yes. We use industry-standard encryption and are fully GDPR-compliant. Your data is stored securely and never shared without your permission.'
    },
    {
      question: 'Can athletes see other athletes\' data?',
      answer: 'No. Athletes can only view their own data. Coaches can view data for athletes they manage.'
    },
    {
      question: 'Do you offer discounts for clubs or federations?',
      answer: 'Yes! Contact us for custom pricing for clubs, national teams, and federations.'
    }
  ];

  return (
    <WelcomeContainer>
      <ScrollAnimateStyles />
      <Header>
        <HeaderContainer>
          <Logo onClick={handleLogoClick}>
            <LogoSJ>sj</LogoSJ>
            <LogoMate>Mate</LogoMate>
          </Logo>
          <NavLinks>
            <NavHomeIcon onClick={scrollToHome} title="Home">
              <Icon component={AiFillHome} />
            </NavHomeIcon>
            {!isMobile && (
              <>
                <NavLink onClick={scrollToFeatures}>Features</NavLink>
                <NavLink onClick={scrollToFAQ}>FAQ</NavLink>
              </>
            )}
            <NavLink 
              onClick={() => {
                navigate('/pricing');
                setTimeout(() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }, 100);
              }}
              style={{ 
                display: isMobile ? 'flex' : 'block',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: isMobile ? '1.2rem' : '0.85rem'
              }}
            >
              {isMobile ? '$' : 'Pricing'}
            </NavLink>
          </NavLinks>
          <NavButtons>
            {user ? (
              <DashboardButton onClick={handleDashboard}>
                Dashboard
                <DashboardBadge>{user.firstName?.charAt(0).toUpperCase() || 'U'}</DashboardBadge>
              </DashboardButton>
            ) : (
              <>
                <SignInButton onClick={handleSignIn}>Sign In</SignInButton>
                <PrimaryButton onClick={handleSignUp}>Start For Free</PrimaryButton>
              </>
            )}
          </NavButtons>
        </HeaderContainer>
      </Header>

      {/* Hero Section */}
      <HeroSection>
        <HeroContent>
          <HeroTitle>
            Track Every Jump.<br/>Analyze Every Detail.
          </HeroTitle>
          <HeroSubtitle>
            sjMate is the complete performance tracking platform for ski jumping coaches and athletes. Import training data, visualize progress and unlock insights that drive results.
          </HeroSubtitle>
          <HeroButtons>
            <PrimaryButton onClick={handleSignUp}>Start For Free</PrimaryButton>
            <SecondaryButton onClick={() => navigate('/how-to-start')}>How to Start</SecondaryButton>
          </HeroButtons>
        </HeroContent>
        <HeroGraphContainer>
          <HeroImageWrapper>
            <HeroImage
              src={`${process.env.PUBLIC_URL || ''}/Hero-image-sjMate.png`}
              alt="sjMate app on laptop and mobile"
              onLoad={(e) => {
                const img = e.currentTarget;
                img.style.maxWidth = `${img.naturalWidth}px`;
                img.style.maxHeight = `${img.naturalHeight}px`;
              }}
            />
          </HeroImageWrapper>
        </HeroGraphContainer>
      </HeroSection>

      {/* Section Divider */}
      <SectionDivider>
        <DividerAccent>
          <DividerHorizontalLine />
          <DividerSquare />
          <DividerHorizontalLine />
        </DividerAccent>
      </SectionDivider>

      {/* Tagline: Stop losing your data in Spreadsheets */}
      <TaglineSection className="scroll-animate">
        <TaglineTitle>Stop losing your data in Spreadsheets.</TaglineTitle>
        <TaglineSubline>There is a better way.</TaglineSubline>
      </TaglineSection>

      {/* Section Divider */}
      <SectionDivider>
        <DividerAccent>
          <DividerHorizontalLine />
          <DividerSquare />
          <DividerHorizontalLine />
        </DividerAccent>
      </SectionDivider>

      {/* Key Features Section */}
      <Section ref={featuresRef} className="scroll-animate">
        <SectionTitle>Everything You Need to Track, Analyze, and Improve</SectionTitle>
        <FeaturesGrid>
          <FeatureSection $reverse className="scroll-animate">
            <FeatureContentWrapper>
              <FeatureTitle>Performance Analytics</FeatureTitle>
              <FeatureDescription>
                Visualize trends with graphs, charts, and detailed breakdowns. Track your progress over 
                time, identify patterns in your performance and understand what factors contribute to 
                your best jumps. Compare sessions, analyze wind impact, and discover insights that help you 
                improve consistently.
              </FeatureDescription>
            </FeatureContentWrapper>
            <FeatureImage 
              src="/App_Screenshot_Analytics_Dashboard.png" 
              alt="Performance Analytics - Visualize trends with graphs, charts, and detailed breakdowns"
            />
          </FeatureSection>

          <FeatureSection className="scroll-animate">
            <FeatureContentWrapper>
              <FeatureTitle>Quick Data Import</FeatureTitle>
              <FeatureDescription>
                Upload training and competition sessions data in seconds. Use our intuitive data entry forms and all your jump data 
                including starting gate, distance, wind conditions, speed, and equipment details are 
                automatically organized and ready for analysis.
              </FeatureDescription>
            </FeatureContentWrapper>
            <FeatureImage 
              src="/App_Screenshot_Data_Import.png" 
              alt="Data Import - Upload training session data in seconds"
            />
          </FeatureSection>

          <FeatureSection $reverse className="scroll-animate">
            <FeatureContentWrapper>
              <FeatureTitle>Training Planner</FeatureTitle>
              <FeatureDescription>
                Plan training sessions, set goals, and track completion of sessions. Create comprehensive 
                training schedules, assign exercises to athletes, and monitor progress in real-time. 
                Set performance targets, schedule competitions, and ensure your athletes are always 
                prepared for their next jump.
              </FeatureDescription>
            </FeatureContentWrapper>
            <FeatureImage 
              src="/App_Screenshot_Training_Planner.png" 
              alt="Training Planner - Plan training sessions, set goals, and track completion"
            />
          </FeatureSection>

          <FeatureSection className="scroll-animate">
            <FeatureContentWrapper>
              <FeatureTitle>Banana Cup 🍌</FeatureTitle>
              <FeatureDescription>
                Gamify training with team challenges and competitions. Create internal 
                competitions, track rankings and motivate athletes through friendly competition. 
                Organize team events and celebrate achievements together. 
                Perfect for clubs and training groups looking to boost engagement.
              </FeatureDescription>
            </FeatureContentWrapper>
            <FeatureImage 
              src="/App_Screenshot_BananaCup.png" 
              alt="Banana Cup - Gamify training with team challenges and competitions"
            />
          </FeatureSection>

          <FeatureSection $reverse className="scroll-animate">
            <FeatureContentWrapper>
              <FeatureTitle>Multi-Athlete Management</FeatureTitle>
              <FeatureDescription>
                Coaches can add unlimited number of athletes with free or paid subscriptions. View all your athletes' 
                performance in one centralized dashboard, compare progress across your team, and 
                identify talent. More athletes mean more data to analyze and improve.
              </FeatureDescription>
            </FeatureContentWrapper>
            <FeatureImage 
              src="/App_Screenshot_Athletes.png" 
              alt="Multi-Athlete Management - View all your athletes' performance in one centralized dashboard"
            />
          </FeatureSection>

          <FeatureSection className="scroll-animate">
            <FeatureContentWrapper>
              <FeatureTitle>Equipment Management</FeatureTitle>
              <FeatureDescription>
                Track and manage all your ski jumping equipment in one place. Record usage of skis, suits, shoes and bindings
                with detailed specifications. Monitor equipment usage and understand how different equipment affects performance. Keep everything 
                organized and make informed decisions about gear selection for optimal results.
              </FeatureDescription>
            </FeatureContentWrapper>
            <FeatureImage 
              src="/App_Screenshot_Equipment.png" 
              alt="Equipment Management - Track and manage all your ski jumping equipment"
            />
          </FeatureSection>

        </FeaturesGrid>
      </Section>

      {/* Section Divider */}
      <SectionDivider>
        <DividerAccent>
          <DividerHorizontalLine />
          <DividerSquare />
          <DividerHorizontalLine />
        </DividerAccent>
      </SectionDivider>

      {/* Solution Overview Section */}
      <SolutionSection ref={solutionRef} className="scroll-animate">
        <SectionTitle>One Platform. Complete Performance Tracking.</SectionTitle>
        <SectionSubtitle style={{ marginBottom: '4rem' }}>
          Built for everyone in the ski jumping community.
        </SectionSubtitle>
        <ThreeColumnGrid>
          <FeatureCard>
            <FeatureCardTitle>
              <FeatureCardIcon>
                <Icon component={FiUser} />
              </FeatureCardIcon>
              For Coaches
            </FeatureCardTitle>
            <FeatureCardList>
              <FeatureCardItem>Import training and competition data in seconds (choose athlete, import starting gate, distance, wind, speed and which equipment was used)</FeatureCardItem>
              <FeatureCardItem>View all your athletes' performance in one dashboard</FeatureCardItem>
              <FeatureCardItem>Track progress with powerful analytics and graphs</FeatureCardItem>
              <FeatureCardItem>Plan training sessions in advance with built-in planner</FeatureCardItem>
              <FeatureCardItem>Have internal Banana Cup competitions</FeatureCardItem>
            </FeatureCardList>
          </FeatureCard>
          <FeatureCard className="scroll-animate">
            <FeatureCardTitle>
              <FeatureCardIcon>
                <Icon component={FiUsers} />
              </FeatureCardIcon>
              For Athletes
            </FeatureCardTitle>
            <FeatureCardList>
              <FeatureCardItem>Import training and competition data in seconds (Import starting gate, distance, wind, speed and which equipment was used)</FeatureCardItem>
              <FeatureCardItem>See your own jumping data and performance trends</FeatureCardItem>
              <FeatureCardItem>Understand what's working (and what's not)</FeatureCardItem>
              <FeatureCardItem>Set goals and track improvement over time</FeatureCardItem>
              <FeatureCardItem>Compete in Banana Cup competitions</FeatureCardItem>
            </FeatureCardList>
          </FeatureCard>
          <FeatureCard className="scroll-animate">
            <FeatureCardTitle>
              <FeatureCardIcon>
                <Icon component={FiBriefcase} />
              </FeatureCardIcon>
              For Clubs & Federations
            </FeatureCardTitle>
            <FeatureCardList>
              <FeatureCardItem>Centralized data for entire nation or club</FeatureCardItem>
              <FeatureCardItem>Unlimited number of coaches and athletes</FeatureCardItem>
              <FeatureCardItem>Long-term performance insights across seasons</FeatureCardItem>
              <FeatureCardItem>Adjusted pricing for different number of users</FeatureCardItem>
            </FeatureCardList>
            <FeatureCardButtonWrapper>
              <PrimaryButton onClick={scrollToContact}>Contact Us for More</PrimaryButton>
            </FeatureCardButtonWrapper>
          </FeatureCard>
        </ThreeColumnGrid>
      </SolutionSection>

      {/* Section Divider */}
      <SectionDivider>
        <DividerAccent>
          <DividerHorizontalLine />
          <DividerSquare />
          <DividerHorizontalLine />
        </DividerAccent>
      </SectionDivider>

      {/* How It Works Section */}
      <Section ref={stepsRef} className="scroll-animate">
        <SectionTitle>Get Started in 3 Simple Steps</SectionTitle>
        <StepsContainer>
          <StepCard className="scroll-animate">
            <StepNumber>1</StepNumber>
            <StepTitle>Sign Up</StepTitle>
            <StepDescription>
              Create your free account as a coach or an athlete in under 60 seconds.
            </StepDescription>
          </StepCard>
          <StepCard className="scroll-animate">
            <StepNumber>2</StepNumber>
            <StepTitle>Import Data</StepTitle>
            <StepDescription>
              Add your athletes and upload your training session data (starting gate, distance, wind, speed and equipment).
            </StepDescription>
          </StepCard>
          <StepCard className="scroll-animate">
            <StepNumber>3</StepNumber>
            <StepTitle>Analyze & Improve</StepTitle>
            <StepDescription>
              View performance graphs, track trends, and unlock insights that drive results.
            </StepDescription>
          </StepCard>
        </StepsContainer>
      </Section>

      {/* Section Divider */}
      <SectionDivider>
        <DividerAccent>
          <DividerHorizontalLine />
          <DividerSquare />
          <DividerHorizontalLine />
        </DividerAccent>
      </SectionDivider>

      {/* FREE Plan Section */}
      <FreePlanSection ref={freePlanRef} className="scroll-animate">
        <FreePlanContent>
          <FreePlanTitle>Start Free. No Credit Card Required.</FreePlanTitle>
          <FreePlanText>
            <p>
              Get started with sjMate&apos;s free plan and experience the power of organized training data management. 
              With our <strong>FREE plan</strong>, you can add athletes to your account, create training sessions for both{' '}
              <strong>ski jumping</strong> and <strong>ground training</strong>, and view all your training data in organized lists 
              on each training session page—everything you need to get started, with the option to upgrade anytime to unlock advanced features.
            </p>
          </FreePlanText>
          <FreePlanButtonWrapper>
            <FreePlanButton onClick={handleSignUp}>START FOR FREE</FreePlanButton>
          </FreePlanButtonWrapper>
        </FreePlanContent>
      </FreePlanSection>

      {/* Section Divider */}
      <SectionDivider>
        <DividerAccent>
          <DividerHorizontalLine />
          <DividerSquare />
          <DividerHorizontalLine />
        </DividerAccent>
      </SectionDivider>

      {/* Security Section */}
      <Section ref={securityRef} className="scroll-animate">
        <SectionTitle>Secure & Private</SectionTitle>
        <SectionSubtitle style={{ marginBottom: '3rem' }}>
          Your data security and privacy are our top priorities
        </SectionSubtitle>
        <SecurityGrid>
          <SecurityItem className="scroll-animate">
            <SecurityIcon>
              <Icon component={FiLock} />
            </SecurityIcon>
            <SecurityTitle>End-to-End Encryption</SecurityTitle>
            <SecurityDescription>
              All your training data and athlete information is encrypted using industry-standard protocols to ensure maximum security.
            </SecurityDescription>
          </SecurityItem>
          <SecurityItem className="scroll-animate">
            <SecurityIcon>
              <Icon component={FiShield} />
            </SecurityIcon>
            <SecurityTitle>GDPR Compliant</SecurityTitle>
            <SecurityDescription>
              We follow strict data protection regulations. Your data stays yours, and you have complete control over your information.
            </SecurityDescription>
          </SecurityItem>
          <SecurityItem className="scroll-animate">
            <SecurityIcon>
              <Icon component={FiDatabase} />
            </SecurityIcon>
            <SecurityTitle>Secure Backups</SecurityTitle>
            <SecurityDescription>
              Regular automated backups ensure your valuable training data is never lost, with multiple redundant storage systems.
            </SecurityDescription>
          </SecurityItem>
          <SecurityItem className="scroll-animate">
            <SecurityIcon>
              <Icon component={FiCheckCircle} />
            </SecurityIcon>
            <SecurityTitle>Privacy Controls</SecurityTitle>
            <SecurityDescription>
              Complete data privacy controls let you manage who can access your information and how it's shared.
            </SecurityDescription>
          </SecurityItem>
        </SecurityGrid>
      </Section>

      {/* Section Divider */}
      {/* Commented out - hiding stats section until we have real users */}
      {/* <SectionDivider>
        <DividerAccent>
          <DividerHorizontalLine />
          <DividerSquare />
          <DividerHorizontalLine />
        </DividerAccent>
      </SectionDivider> */}

      {/* Social Proof Section */}
      {/* Commented out - hiding stats section until we have real users */}
      {/* <Section ref={statsRef} className="scroll-animate">
        <SectionTitle>Trusted by Coaches and Athletes Worldwide</SectionTitle>
        <StatsContainer>
          <StatCard className="scroll-animate">
            <StatNumber>1000+</StatNumber>
            <StatLabel>Jumps Tracked</StatLabel>
          </StatCard>
          <StatCard className="scroll-animate">
            <StatNumber>20+</StatNumber>
            <StatLabel>Athletes Using sjMate</StatLabel>
          </StatCard>
          <StatCard className="scroll-animate">
            <StatNumber>5+</StatNumber>
            <StatLabel>Clubs Onboard</StatLabel>
          </StatCard>
        </StatsContainer>
        <TestimonialsGrid>
          <TestimonialCard>
            <TestimonialText>
              "Finally, a tool built specifically for ski jumping. sjMate saves me hours every week."
            </TestimonialText>
            <TestimonialAuthor>— Coach Joeri Schouten</TestimonialAuthor>
            <TestimonialRole>Ski Club De Oude Meent</TestimonialRole>
          </TestimonialCard>
          <TestimonialCard>
            <TestimonialText>
              "I can see exactly where I'm improving and what I need to work on. Game changer."
            </TestimonialText>
            <TestimonialAuthor>— Athlete Name</TestimonialAuthor>
            <TestimonialRole>Country</TestimonialRole>
          </TestimonialCard>
        </TestimonialsGrid>
      </Section> */}

      {/* Section Divider */}
      <SectionDivider>
        <DividerAccent>
          <DividerHorizontalLine />
          <DividerSquare />
          <DividerHorizontalLine />
        </DividerAccent>
      </SectionDivider>

      {/* FAQ Section */}
      <Section ref={faqRef} className="scroll-animate">
        <SectionTitle>Frequently Asked Questions</SectionTitle>
        <FAQContainer>
          {faqs.map((faq, index) => (
            <FAQItem key={index} className="scroll-animate">
              <FAQQuestion onClick={() => toggleFAQ(index)}>
                {faq.question}
                <span>{openFAQ === index ? '−' : '+'}</span>
              </FAQQuestion>
              {openFAQ === index && (
                <FAQAnswer>
                  {typeof faq.answer === 'string' ? faq.answer : faq.answer}
                </FAQAnswer>
              )}
            </FAQItem>
          ))}
        </FAQContainer>
      </Section>

      {/* Section Divider */}
      <SectionDivider>
        <DividerAccent>
          <DividerHorizontalLine />
          <DividerSquare />
          <DividerHorizontalLine />
        </DividerAccent>
      </SectionDivider>

      {/* Final CTA Section */}
      <CTASection ref={ctaRef} className="scroll-animate">
        <CTATitle>Ready to Elevate Your Training?</CTATitle>
        <CTASubtitle>
          Join coaches and athletes who are already using sjMate to track performance and unlock insights.
        </CTASubtitle>
        <CTAButton onClick={handleSignUp}>Start For Free</CTAButton>
        <CTANote>No credit card required. Upgrade anytime.</CTANote>
      </CTASection>

      {/* Coming Features Section */}
      <Section ref={comingFeaturesRef} className="scroll-animate">
        <SectionTitle>New Features in Development</SectionTitle>
        <SectionSubtitle style={{ marginBottom: '3rem' }}>
          We're constantly improving sjMate. Here's what's coming next:
        </SectionSubtitle>
        <FeaturesGrid>
          <FeatureSection $reverse className="scroll-animate">
            <FeatureContentWrapper>
              <FeatureTitle>AI Mate</FeatureTitle>
              <FeatureDescription>
                Get detailed insights and answers about your training data with our AI-powered assistant.
                Ask questions about your performance, get personalized recommendations, and unlock deeper
                understanding of your training patterns. Our AI Mate analyzes your data to provide
                actionable insights that help you improve faster and make data-driven decisions.
              </FeatureDescription>
            </FeatureContentWrapper>
            <FeatureImage
              src="/App_Screenshot_AI_Chat.png"
              alt="AI Mate - Get detailed insights and answers about your training data"
            />
          </FeatureSection>
        </FeaturesGrid>
        <StepsContainer>
          <StepCard>
            <StepNumber>
              <Icon component={FiMessageCircle} />
            </StepNumber>
            <StepTitle>Customized AI Mate</StepTitle>
            <StepDescription>
              Enhanced AI capabilities with deeper insights and more accurate recommendations for your training.
            </StepDescription>
          </StepCard>
          <StepCard className="scroll-animate">
            <StepNumber>
              <Icon component={FiVideo} />
            </StepNumber>
            <StepTitle>Option to Video Record Jumps</StepTitle>
            <StepDescription>
              Record and analyze your jumps with video integration for comprehensive performance review.
            </StepDescription>
          </StepCard>
          <StepCard className="scroll-animate">
            <StepNumber>
              <Icon component={FiActivity} />
            </StepNumber>
            <StepTitle>More Detailed Ground Training Sessions</StepTitle>
            <StepDescription>
              Track and analyze ground training with enhanced detail and comprehensive metrics.
            </StepDescription>
          </StepCard>
        </StepsContainer>
      </Section>

      {/* Section Divider */}
      <SectionDivider>
        <DividerAccent>
          <DividerHorizontalLine />
          <DividerSquare />
          <DividerHorizontalLine />
        </DividerAccent>
      </SectionDivider>

      {/* Contact Form Section */}
      <Section ref={contactRef} className="scroll-animate">
        <SectionTitle>Get in Touch</SectionTitle>
        <SectionSubtitle style={{ marginBottom: '3rem' }}>
          Have questions or feedback? We'd love to hear from you!
        </SectionSubtitle>
        <ContactFormContainer>
          <ContactForm onSubmit={handleContactSubmit}>
            <ContactFormGroup>
              <ContactLabel htmlFor="name">Name</ContactLabel>
              <ContactInput
                type="text"
                id="name"
                name="name"
                value={contactForm.name}
                onChange={handleContactChange}
                required
                placeholder="Your name"
              />
            </ContactFormGroup>
            <ContactFormGroup>
              <ContactLabel htmlFor="email">Email</ContactLabel>
              <ContactInput
                type="email"
                id="email"
                name="email"
                value={contactForm.email}
                onChange={handleContactChange}
                required
                placeholder="your.email@example.com"
              />
            </ContactFormGroup>
            <ContactFormGroup>
              <ContactLabel htmlFor="subject">Subject</ContactLabel>
              <ContactInput
                type="text"
                id="subject"
                name="subject"
                value={contactForm.subject}
                onChange={handleContactChange}
                required
                placeholder="What's this about?"
              />
            </ContactFormGroup>
            <ContactFormGroup>
              <ContactLabel htmlFor="message">Message</ContactLabel>
              <ContactTextarea
                id="message"
                name="message"
                value={contactForm.message}
                onChange={handleContactChange}
                required
                rows={6}
                placeholder="Tell us more..."
              />
            </ContactFormGroup>
            {submitStatus === 'success' && (
              <ContactSuccessMessage>Thank you! Your message has been sent successfully.</ContactSuccessMessage>
            )}
            {submitStatus === 'error' && (
              <ContactErrorMessage>Something went wrong. Please try again later.</ContactErrorMessage>
            )}
            <ContactSubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </ContactSubmitButton>
          </ContactForm>
        </ContactFormContainer>
      </Section>

      {/* Footer */}
      <Footer>
        <FooterContent>
          <FooterSection>
            <FooterLogo>
              <FooterLogoSJ>sj</FooterLogoSJ>
              <FooterLogoMate>Mate</FooterLogoMate>
            </FooterLogo>
            <div style={{ marginTop: '2.5rem' }}>
              <FooterTitle>About sjMate</FooterTitle>
              <FooterLink 
                href="#" 
                onClick={(e) => {
                  e.preventDefault();
                  scrollToFeatures();
                }}
                style={{ cursor: 'pointer' }}
              >
                Features
              </FooterLink>
              <FooterLink 
                href="/pricing" 
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/pricing');
                }}
                style={{ cursor: 'pointer' }}
              >
                Pricing
              </FooterLink>
              <FooterLink 
                href="/how-to-start" 
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/how-to-start');
                }}
                style={{ cursor: 'pointer' }}
              >
                How It Works
              </FooterLink>
            </div>
          </FooterSection>
          <FooterSection>
            <div style={{ marginTop: '2.5rem' }}>
              <FooterTitle>Legal</FooterTitle>
              <FooterLink href="/privacy-policy">Privacy Policy</FooterLink>
              <FooterLink href="/terms-of-service">Terms of Service</FooterLink>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent('sjmate-open-cookie-settings'))}
                style={{ background: 'none', border: 'none', padding: 0, marginTop: '0.15rem', font: 'inherit', color: 'var(--text-muted)', textDecoration: 'none', cursor: 'pointer', fontSize: '0.95rem', fontWeight: 600, display: 'block', lineHeight: 1.4 }}
              >
                Cookie settings
              </button>
            </div>
          </FooterSection>
          <FooterSection>
            <div style={{ marginTop: '2.5rem' }}>
              <FooterTitle>Contact</FooterTitle>
              <FooterLink href="mailto:skijumpingmate@gmail.com">skijumpingmate@gmail.com</FooterLink>
              <FooterLink href="https://instagram.com/sjmate" target="_blank" rel="noopener noreferrer">
                <Icon component={FiInstagram} style={{ display: 'inline-block', marginRight: '0.5rem', verticalAlign: 'middle' }} />
                Instagram
              </FooterLink>
            </div>
          </FooterSection>
        </FooterContent>
        <FooterBottom>
        <FooterText>
          Created and designed in 2026 by <b>z-epic</b>
        </FooterText>
        </FooterBottom>
        <FooterLine />
      </Footer>
    </WelcomeContainer>
  );
};

export default WelcomeScreen; 
